import { IconAlertTriangle, IconBan, IconChevronDown, IconExternalLink, IconPlayerPlayFilled, IconSquareRoundedX } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { TERMINAL_NEGATIVOS } from "@/components/solicitudes/stepper/steps-config";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Group,
  IconAction,
  Stack,
  Td,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tr,
  TruncatedTooltip,
  useConfirmDialog,
} from "@/components/ui";
import { TONO_HEX, tonoDeEstado } from "@/lib/estado-color";
import { MotivosForm } from "@/features/autorizacion/components/MotivosForm";
import { useDarDeBaja } from "@/features/autorizacion/hooks/use-autorizacion-mutations";
import { useCancelarSolicitud } from "@/features/solicitudes/hooks/use-cancelar-solicitud";
import { ESTADOS_BAJA_POR_MESA_CONTROL, ESTADOS_CANCELABLES_POR_SOLICITANTE } from "@/features/solicitud-detalle/utils/estado-flags";
import { useAuthStore } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { ESTADOS_EN_AUTORIZACION, etiquetaEstado } from "@/lib/estado-labels";
import type { MiSolicitud } from "@/lib/types";
import { formatFecha } from "@/shared/format/fecha";
import { formatearMonto } from "@/shared/format/money";
import { sustantivoDetalle, tipoDetalleDe } from "../utils/detalle-expandible";
import { BotonInfoCaOc } from "./BotonInfoCaOc";

/** Etiqueta amigable del tipo de solicitud · el enum crudo va pegado
 *  ("ContratoAnualizado") y se muestra con espacios. */
function etiquetaTipo(tipo: string | null | undefined): string {
  if (!tipo) return "";
  if (tipo === "ContratoAnualizado") return "Contrato Anualizado";
  if (tipo === "OrdenDeCompra") return "Orden de Compra";
  return tipo;
}

/** Etiqueta amigable del subtipo · el enum crudo va con guion bajo
 *  ("CFDI_UNICO", "MULTIPLES_CFDI") y se muestra con espacios (y acento). */
function etiquetaSubtipo(subtipo: string | null | undefined): string {
  if (!subtipo) return "";
  if (subtipo === "CFDI_UNICO") return "CFDI ÚNICO";
  return subtipo.replace(/_/g, " ");
}

/**
 * URL del paso donde el solicitante debe *continuar* la solicitud según su
 * estado actual (pre-envío). Devuelve `null` para estados ya enviados o
 * terminales, donde no hay nada que continuar. Path concreto (id sustituido).
 */
function pathContinuar(s: MiSolicitud): string | null {
  const base = `/solicitudes/${s.id}`;
  const esMultiple = s.subtipo === "MULTIPLES_CFDI";
  const esCA = s.tipo === "ContratoAnualizado";
  const esOC = s.tipo === "OrdenDeCompra";
  switch (s.estado) {
    case "SubtipoSeleccionado":
      if (esCA) return `${base}/contrato-anualizado`;
      if (esOC) return `${base}/orden-compra`;
      return esMultiple ? `${base}/cargar-zip` : `${base}/cargar-cfdi`;
    case "ContratoAnualizadoSeleccionado":
      return `${base}/cargar-cfdi`;
    case "OrdenDeCompraSeleccionada":
      return `${base}/cargar-cfdi`;
    case "Iniciada":
    case "TipoSeleccionado":
    case "CfdiPendiente":
    case "CfdiCargado":
    case "EnValidacionReglas":
    case "ReglasRechazadas":
    case "EnValidacionFiscal":
    case "RechazadoFiscal":
    case "SuspendidaFiscal":
      return esMultiple ? `${base}/cargar-zip` : `${base}/cargar-cfdi`;
    case "Validado":
    case "CapturandoDatos":
      return `${base}/datos-generales`;
    case "SeleccionandoProveedor":
      return `${base}/proveedor`;
    case "Prorrateando":
      return esMultiple ? `${base}/prorrateo-multiple` : `${base}/prorrateo`;
    case "ProvisionalmenteListo":
    case "AdjuntandoDocumentos":
      return `${base}/documentos`;
    default:
      return null;
  }
}

/** `n/m` del flujo de autorización cuando aporta (ni terminal negativo ni ya Autorizada). */
function nivelFlujo(s: MiSolicitud): string | null {
  if (!s.flujoNivelActual || !s.flujoCantidadNiveles) return null;
  if (s.estado === "Autorizada" || TERMINAL_NEGATIVOS.has(s.estado)) return null;
  return `${s.flujoNivelActual}/${s.flujoCantidadNiveles}`;
}

/**
 * Overrides SOLO para el badge compacto de la tabla · el nombre completo
 * del estado sigue viviendo en el tooltip (`tooltipEstado`).
 */
const LABEL_COMPACTO: Record<string, string> = {
  ReglasRechazadas: "Rechazada",
};

/**
 * Etiqueta compacta para el badge de la tabla · en autorización el nivel
 * sustituye al ordinal verboso ("En autorización primer nivel" →
 * "Autorización 1/3"); en el resto, el contador se anexa al nombre.
 */
function etiquetaEstadoCompacta(s: MiSolicitud): string {
  const nivel = nivelFlujo(s);
  const base = LABEL_COMPACTO[s.estado];
  if (!nivel) return base ?? etiquetaEstado(s.estado, { nivelActual: s.flujoNivelActual, rolActual: s.flujoRolActual });
  if (ESTADOS_EN_AUTORIZACION.has(s.estado)) return `Autorización ${nivel}`;
  return `${base ?? etiquetaEstado(s.estado)} · ${nivel}`;
}

/** Tooltip del badge · nombre completo del estado + nivel explícito. */
function tooltipEstado(s: MiSolicitud): string {
  const completa = etiquetaEstado(s.estado, { nivelActual: s.flujoNivelActual, rolActual: s.flujoRolActual });
  const nivel = nivelFlujo(s);
  return nivel ? `${completa} · nivel ${s.flujoNivelActual} de ${s.flujoCantidadNiveles}` : completa;
}

/** Fila de la tabla desktop · presentacional. */
export function SolicitudRow({
  s,
  expandida = false,
  detalleId,
  onAlternarExpandida,
}: {
  s: MiSolicitud;
  /** Fila expandible (facturas de la solicitud) · la fila de detalle la pinta `SolicitudesTable`. */
  expandida?: boolean;
  /** Id del `<tr>` de detalle · destino del `aria-controls` del chevron. */
  detalleId?: string;
  onAlternarExpandida?: () => void;
}) {
  const navigate = useNavigate();
  const user = useAuthStore((st) => st.user);
  const cancelar = useCancelarSolicitud(s.id);
  const baja = useDarDeBaja(s.id, { onBaja: () => setBajaOpen(false) });
  const [bajaOpen, setBajaOpen] = useState(false);
  const confirm = useConfirmDialog();

  const irADetalle = () => navigate({ to: "/solicitudes/$id", params: { id: s.id } });
  const tipoDetalle = tipoDetalleDe(s);

  // Hotspot 90 · las acciones se deciden por PERMISO (P46), no por rol:
  //  - Continuar/Cancelar → `solicitudes.gestionar` + ser el DUEÑO (el dominio y los
  //    endpoints del wizard solo aceptan al solicitante dueño; quien ve todas —P29— no
  //    debe ver esas acciones sobre solicitudes ajenas).
  //  - Dar de baja → `autorizacion.dar_de_baja` (mismo permiso que exige el endpoint).
  const permisos = user?.permisos ?? [];
  const gestionaPropia = permisos.includes(PERMISOS.solicitudesGestionar) && (s.esPropia ?? false);
  const urlContinuar = gestionaPropia ? pathContinuar(s) : null;
  const puedeCancelar = gestionaPropia && ESTADOS_CANCELABLES_POR_SOLICITANTE.has(s.estado);
  const puedeDarDeBaja =
    permisos.includes(PERMISOS.autorizacionDarDeBaja) && ESTADOS_BAJA_POR_MESA_CONTROL.has(s.estado);

  const abrirCancelar = async () => {
    const ok = await confirm({
      title: `Cancelar solicitud ${s.folio}`,
      message: (
        <>
          ¿Seguro que deseas cancelar esta solicitud? Esta acción es <b>terminal</b>.
        </>
      ),
      confirmLabel: "Cancelar solicitud",
      cancelLabel: "Volver",
      confirmColor: "peligro",
    });
    if (ok) cancelar.mutate([]);
  };

  return (
    <Tr
      style={{
        // Realce neutro de "rechazo pendiente" · gris adaptable al esquema
        // (claro: gris tenue · oscuro: panel un poco más claro que el fondo)
        // en vez de un tinte naranja que se veía café sobre el modo oscuro.
        // El punto naranja sigue marcando la atención con la marca.
        background: s.tieneRechazoPendiente ? "light-dark(var(--color-slate-100), var(--color-ink-800))" : undefined,
      }}
      className={expandida ? "bg-[color-mix(in_oklab,var(--color-slate-500)_8%,transparent)]" : undefined}
    >
      {/* Chevron de la fila expandible (patrón IBM Carbon): muestra/oculta las
          facturas de la solicitud (o, en el subtipo Invoice, los datos y el PDF del
          invoice) en la fila de detalle de abajo. Solo aparece si hay algo que mostrar;
          la celda se conserva para no desalinear columnas. */}
      <Td className="!px-1 align-middle">
        {tipoDetalle && (
        <IconAction
          label={`${expandida ? "Ocultar" : "Ver"} ${sustantivoDetalle(tipoDetalle)} de la solicitud ${s.folio}`}
          size="sm"
          aria-expanded={expandida}
          aria-controls={expandida ? detalleId : undefined}
          onClick={onAlternarExpandida}
          className="mx-auto"
        >
          <IconChevronDown
            size={16}
            stroke={2}
            className={`transition-transform duration-150 motion-reduce:transition-none ${expandida ? "rotate-180" : ""}`}
          />
        </IconAction>
        )}
      </Td>
      <Td>
        {/* Sin punto de "rechazo pendiente" junto al folio (lo pidió el
            usuario): el realce ya lo dan el fondo gris de la fila y el punto
            semántico de la columna Estado. Padding por defecto (igual que las
            demás celdas) para que la separación entre columnas sea uniforme. */}
        <Text size="sm" fw={500} className="whitespace-nowrap">
          {s.folio}
        </Text>
      </Td>
      <Td>
        {/* Estado como PUNTO + texto plano (alternativa A elegida por el
            usuario · estilo Linear/GitHub) en vez de pill relleno: 15 barras
            sólidas idénticas se leían como una pared azul. El punto carga el
            color por estado de la FUENTE ÚNICA compartida (`tonoDeEstado` en
            `lib/estado-color.ts` · misma paleta que "Mis últimas solicitudes"
            y el donut: verde=autorización, azul=en proceso, índigo=autorizada,
            rojo=rechazos, ámbar=SAT, morado=armado, slate=captura/terminales);
            el texto usa el foreground normal. Se conservan el label compacto
            con nivel n/m, el truncado y el nombre completo en el tooltip. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex min-w-0 items-start gap-2">
              <span
                className="mt-1 size-2 shrink-0 rounded-full"
                style={{ backgroundColor: TONO_HEX[tonoDeEstado(s.estado)] }}
              />
              {/* El estado NUNCA se trunca: hace wrap a 2 líneas si hace falta,
                  para no quedar ambiguo ("AUTORIZA…"). */}
              <Text size="xs" fw={600} className="whitespace-normal break-words leading-tight tracking-wide">
                {etiquetaEstadoCompacta(s)}
              </Text>
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltipEstado(s)}</TooltipContent>
        </Tooltip>
      </Td>
      <Td>
        <TruncatedTooltip label={etiquetaTipo(s.tipo)} disabled={!s.tipo}>
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {etiquetaTipo(s.tipo)}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {etiquetaSubtipo(s.subtipo)}
            </Text>
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        {/* CA/OC · número del contrato anualizado o de la orden de compra (según el tipo). */}
        {s.claveCaOc && (
          <div className="flex items-center gap-1">
            <Text size="sm" fw={600} ff="mono" className="whitespace-nowrap">
              {s.claveCaOc}
            </Text>
            <BotonInfoCaOc tipo={s.tipo} clave={s.claveCaOc} />
          </div>
        )}
      </Td>
      <Td>
        <TruncatedTooltip label={s.organizacionNombre ?? ""} disabled={!s.organizacionNombre}>
          <Text size="sm" fw={600} truncate>
            {s.organizacionNombre ?? ""}
          </Text>
        </TruncatedTooltip>
      </Td>
      <Td>
        <TruncatedTooltip label={s.areaNombre ?? ""} disabled={!s.areaNombre}>
          <Text size="sm" truncate>
            {s.areaNombre ?? ""}
          </Text>
        </TruncatedTooltip>
      </Td>
      {/* Proveedor fusionado con su RFC (2ª línea) · mismo patrón que la
          tabla de Mis facturas · libera una columna completa. */}
      <Td>
        <TruncatedTooltip
          label={`${s.proveedorRazonSocial ?? ""}${s.proveedorRfc ? ` · ${s.proveedorRfc}` : ""}`}
          disabled={!s.proveedorRazonSocial}
        >
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {s.proveedorRazonSocial ?? ""}
            </Text>
            {s.proveedorRfc && (
              <Text size="xs" c="dimmed" truncate>
                {s.proveedorRfc}
              </Text>
            )}
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        {s.descripcionPrimerConcepto ? (
          <TruncatedTooltip label={s.descripcionPrimerConcepto}>
            <Text size="sm" truncate>
              {s.descripcionPrimerConcepto}
            </Text>
          </TruncatedTooltip>
        ) : null}
      </Td>
      {/* Total con la MONEDA fusionada (código ISO como 2ª línea) · antes eran
          dos columnas · `narrowSymbol` rinde "$" en MXN/USD y el símbolo real
          en otras divisas, y el código ISO abajo desambigua ($ MXN vs $ USD). */}
      <Td align="right">
        {s.totalCfdi !== null ? (
          <Stack gap={0} className="items-end">
            <Text size="sm" fw={700} className="whitespace-nowrap">
              {formatearMonto(s.totalCfdi, s.monedaCfdi, { minimumFractionDigits: 2, currencyDisplay: "narrowSymbol" })}
            </Text>
            {s.monedaCfdi && (
              <Text size="xs" c="dimmed" className="leading-tight">
                {s.monedaCfdi}
              </Text>
            )}
          </Stack>
        ) : null}
      </Td>
      <Td>
        {s.fechaPago ? (
          <Text size="sm" fw={500}>
            {formatFecha(s.fechaPago)}
          </Text>
        ) : null}
      </Td>
      {/* HOTSPOT-DOMINIO: la columna "Núm. Doc" (número de documento asignado a
          la solicitud) se RETIRÓ de la tabla porque aún no existe fuente del
          dato — era una celda permanentemente vacía. Cuando el dominio lo
          defina, reintroducirla aquí y en `columnas()` de SolicitudesTable. */}
      <Td>
        <Text size="sm">{formatFecha(s.fechaInicio)}</Text>
      </Td>
      {/* Acciones · TODOS los iconos sueltos (sin menú "⋯"), como chips de color.
          Color SEMÁNTICO por acción: Continuar (▶) verde, Ver detalle (↗) azul,
          Cancelar y Dar de baja en rojo (destructivas). Cada destructiva abre su
          propia CONFIRMACIÓN (diálogo), así que no hay ejecución directa por
          click accidental. */}
      <Td align="right">
        <Group gap={6} justify="flex-end" wrap="nowrap">
          {urlContinuar && (
            <IconAction
              label="Continuar solicitud"
              size="md"
              tone="exito"
              onClick={() => navigate({ to: urlContinuar as never })}
            >
              <IconPlayerPlayFilled size={18} />
            </IconAction>
          )}
          <IconAction
            label="Ver detalle"
            size="md"
            tone="info"
            onClick={irADetalle}
          >
            <IconExternalLink size={18} stroke={1.9} />
          </IconAction>
          {puedeCancelar && (
            <IconAction
              label="Cancelar solicitud"
              size="md"
              tone="peligro"
              onClick={() => void abrirCancelar()}
            >
              <IconSquareRoundedX size={18} stroke={1.9} />
            </IconAction>
          )}
          {puedeDarDeBaja && (
            <IconAction
              label="Dar de baja (Mesa de Control)"
              size="md"
              tone="peligro"
              onClick={() => setBajaOpen(true)}
            >
              <IconBan size={18} stroke={1.9} />
            </IconAction>
          )}
        </Group>
      </Td>
      <Dialog open={bajaOpen} onOpenChange={setBajaOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dar de baja solicitud {s.folio}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            {s.enviadaAContabilizacion ? (
              <Alert variant="warning" icon={<IconAlertTriangle size={16} />}>
                La póliza de esta solicitud ya se generó y el archivo ya se depositó en SFTP. Dar de baja NO revierte ese depósito.
              </Alert>
            ) : null}
            <MotivosForm variante="baja" loading={baja.isPending} onCancelar={() => setBajaOpen(false)} onConfirmar={(motivos) => baja.mutate(motivos)} />
          </Stack>
        </DialogContent>
      </Dialog>
    </Tr>
  );
}
